import { get, set, del, keys } from 'idb-keyval';
import { AuditInstance } from '../types';
import { MODULE_1_LECTURES_31_33 } from '../modules/module1_lectures31_33';
import { cloudSyncService } from './cloudSyncService';

const AUDIT_INDEX_KEY = 'seo_audit_platform_audits_index';
const CURRENT_AUDIT_KEY = 'seo_audit_platform_active_audit_id';

export class StorageService {
  /**
   * Get all audits list (metadata summary)
   * Includes automatic recovery from IndexedDB if localStorage was cleared,
   * and auto-seeds the starter audit if no audits exist.
   */
  static async getAllAudits(): Promise<AuditInstance[]> {
    try {
      const auditsMap = new Map<string, AuditInstance>();

      // 1. Try reading from indexed IDs in localStorage
      const auditIdsStr = localStorage.getItem(AUDIT_INDEX_KEY);
      let auditIds: string[] = [];
      if (auditIdsStr) {
        try {
          auditIds = JSON.parse(auditIdsStr);
        } catch {
          auditIds = [];
        }
      }

      for (const id of auditIds) {
        let audit = await get<AuditInstance>(`audit_${id}`);
        if (!audit) {
          audit = await get<AuditInstance>(id);
        }
        if (audit && audit.id) {
          auditsMap.set(audit.id, audit);
        }
      }

      // 2. Scan ALL IndexedDB keys to recover any unindexed or orphaned audits
      try {
        const dbKeys = await keys();
        for (const k of dbKeys) {
          const item = await get<any>(k);
          if (item && typeof item === 'object') {
            if (item.id && (item.clientName || item.moduleId || item.answers)) {
              auditsMap.set(item.id, item as AuditInstance);
            }
          }
        }
      } catch (scanErr) {
        console.warn('IndexedDB key scan notice:', scanErr);
      }

      // Purge any accidental Apex Digital Solutions records
      for (const [id, a] of Array.from(auditsMap.entries())) {
        if (a.clientName?.toLowerCase().includes('apex') || id.toLowerCase().includes('apex')) {
          auditsMap.delete(id);
          try {
            await del(`audit_${id}`);
            await del(id);
          } catch {
            // ignore
          }
        } else if (a.clientName?.includes('Door') || a.websiteUrl?.includes('reflectwindow.com')) {
          a.clientName = 'Reflect Window Cleaning';
          a.websiteUrl = 'https://reflectwindowcleaning.com';
          if (a.answers?.['step-setup-client']?.fieldValues) {
            a.answers['step-setup-client'].fieldValues.clientName = 'Reflect Window Cleaning';
            a.answers['step-setup-client'].fieldValues.websiteUrl = 'https://reflectwindowcleaning.com';
          }
          auditsMap.set(id, a);
          try {
            await set(`audit_${id}`, a);
          } catch {
            // ignore
          }
        }
      }

      let audits = Array.from(auditsMap.values());

      // Auto-seed starter audits if none exist
      if (audits.length === 0) {
        const ut = await this.createUnitedTruckingAudit();
        const rw = await this.createReflectWindowAudit();
        audits = [ut, rw];
      }

      // 3. Re-sync index in localStorage
      if (audits.length > 0) {
        const updatedIds = audits.map(a => a.id);
        localStorage.setItem(AUDIT_INDEX_KEY, JSON.stringify(updatedIds));
      }

      return audits.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    } catch (err) {
      console.error('Failed to get all audits:', err);
      return [];
    }
  }

  /**
   * Get a single audit by ID
   */
  static async getAuditById(auditId: string): Promise<AuditInstance | null> {
    try {
      let audit = await get<AuditInstance>(`audit_${auditId}`);
      if (!audit) {
        audit = await get<AuditInstance>(auditId);
      }
      return audit || null;
    } catch (err) {
      console.error(`Failed to get audit ${auditId}:`, err);
      return null;
    }
  }

  /**
   * Create starter audit for United Trucking Supplies (Trinidad & Tobago)
   */
  static async createUnitedTruckingAudit(): Promise<AuditInstance> {
    const auditId = 'audit_united_trucking';
    const now = new Date().toISOString();

    const starterAudit: AuditInstance = {
      id: auditId,
      clientName: 'United Trucking Supplies',
      websiteUrl: 'https://unitedtruckingsupplies.com',
      createdAt: now,
      updatedAt: now,
      status: 'in_progress',
      moduleId: MODULE_1_LECTURES_31_33.id,
      methodologyVersion: MODULE_1_LECTURES_31_33.version,
      answers: {
        'step-setup-client': {
          stepId: 'step-setup-client',
          fieldValues: {
            clientName: 'United Trucking Supplies',
            websiteUrl: 'https://unitedtruckingsupplies.com',
          },
          calculatedValues: {},
          completed: true,
          completedAt: now,
        },
        'step-titles-missing': {
          stepId: 'step-titles-missing',
          fieldValues: {
            totalPages: 38,
            missingTitlesCount: 0,
            notes: 'All commercial truck parts category pages have indexable title tags.',
          },
          calculatedValues: {
            missingPercentage: 0,
          },
          completed: true,
          completedAt: now,
        },
        'step-titles-duplicate': {
          stepId: 'step-titles-duplicate',
          fieldValues: {
            duplicateTitlesCount: 1,
            notes: 'Minor overlap on aftermarket spare parts category.',
          },
          calculatedValues: {},
          completed: true,
          completedAt: now,
        },
        'step-speed-insights-mobile': {
          stepId: 'step-speed-insights-mobile',
          fieldValues: {
            mobilePerformanceScore: 82,
            lcp: 2.2,
            cls: 0.03,
            fid: 28,
          },
          calculatedValues: {},
          completed: true,
          completedAt: now,
        },
      },
      currentStepId: MODULE_1_LECTURES_31_33.sections[0].steps[1]?.id || MODULE_1_LECTURES_31_33.sections[0].steps[0].id,
    };

    await set(`audit_${auditId}`, starterAudit);
    localStorage.setItem(CURRENT_AUDIT_KEY, auditId);
    return starterAudit;
  }

  /**
   * Create starter audit for Reflect Window Cleaning (Edmonton, AB)
   */
  static async createReflectWindowAudit(): Promise<AuditInstance> {
    const auditId = 'audit_reflect_window';
    const now = new Date().toISOString();

    const starterAudit: AuditInstance = {
      id: auditId,
      clientName: 'Reflect Window Cleaning',
      websiteUrl: 'https://reflectwindowcleaning.com',
      createdAt: now,
      updatedAt: now,
      status: 'in_progress',
      moduleId: MODULE_1_LECTURES_31_33.id,
      methodologyVersion: MODULE_1_LECTURES_31_33.version,
      answers: {
        'step-setup-client': {
          stepId: 'step-setup-client',
          fieldValues: {
            clientName: 'Reflect Window Cleaning',
            websiteUrl: 'https://reflectwindowcleaning.com',
          },
          calculatedValues: {},
          completed: true,
          completedAt: now,
        },
        'step-titles-missing': {
          stepId: 'step-titles-missing',
          fieldValues: {
            totalPages: 18,
            missingTitlesCount: 0,
            notes: 'All residential window cleaning, commercial washing, and gutter cleaning service pages contain unique and indexable title tags.',
          },
          calculatedValues: {
            missingPercentage: 0,
          },
          completed: true,
          completedAt: now,
        },
        'step-speed-insights-mobile': {
          stepId: 'step-speed-insights-mobile',
          fieldValues: {
            mobilePerformanceScore: 89,
            lcp: 1.8,
            cls: 0.01,
            fid: 18,
          },
          calculatedValues: {},
          completed: true,
          completedAt: now,
        },
      },
      currentStepId: MODULE_1_LECTURES_31_33.sections[0].steps[1]?.id || MODULE_1_LECTURES_31_33.sections[0].steps[0].id,
    };

    await set(`audit_${auditId}`, starterAudit);
    return starterAudit;
  }

  /**
   * Create a new audit instance
   */
  static async createAudit(clientName: string, websiteUrl: string): Promise<AuditInstance> {
    const auditId = `audit_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    const now = new Date().toISOString();

    const newAudit: AuditInstance = {
      id: auditId,
      clientName,
      websiteUrl,
      createdAt: now,
      updatedAt: now,
      status: 'in_progress',
      moduleId: MODULE_1_LECTURES_31_33.id,
      methodologyVersion: MODULE_1_LECTURES_31_33.version,
      answers: {
        'step-setup-client': {
          stepId: 'step-setup-client',
          fieldValues: {
            clientName,
            websiteUrl,
          },
          calculatedValues: {},
          completed: true,
          completedAt: now,
        }
      },
      currentStepId: MODULE_1_LECTURES_31_33.sections[0].steps[1]?.id || MODULE_1_LECTURES_31_33.sections[0].steps[0].id,
    };

    // Save audit to IndexedDB
    await set(`audit_${auditId}`, newAudit);

    // Update index in LocalStorage
    const auditIdsStr = localStorage.getItem(AUDIT_INDEX_KEY);
    const auditIds: string[] = auditIdsStr ? JSON.parse(auditIdsStr) : [];
    if (!auditIds.includes(auditId)) {
      auditIds.push(auditId);
      localStorage.setItem(AUDIT_INDEX_KEY, JSON.stringify(auditIds));
    }

    // Set active audit
    localStorage.setItem(CURRENT_AUDIT_KEY, auditId);

    return newAudit;
  }

  /**
   * Save / update an audit instance (autosave)
   */
  static async saveAudit(audit: AuditInstance): Promise<void> {
    try {
      audit.updatedAt = new Date().toISOString();
      await set(`audit_${audit.id}`, audit);
      // Background cloud sync
      cloudSyncService.saveAuditToCloud(audit).catch(() => {});
    } catch (err) {
      console.error(`Failed to save audit ${audit.id}:`, err);
    }
  }

  /**
   * Delete an audit instance
   */
  static async deleteAudit(auditId: string): Promise<void> {
    try {
      await del(`audit_${auditId}`);
      await del(auditId);

      const auditIdsStr = localStorage.getItem(AUDIT_INDEX_KEY);
      if (auditIdsStr) {
        const auditIds: string[] = JSON.parse(auditIdsStr);
        const updated = auditIds.filter(id => id !== auditId && `audit_${id}` !== auditId);
        localStorage.setItem(AUDIT_INDEX_KEY, JSON.stringify(updated));
      }

      if (localStorage.getItem(CURRENT_AUDIT_KEY) === auditId) {
        localStorage.removeItem(CURRENT_AUDIT_KEY);
      }

      // Background cloud delete
      cloudSyncService.deleteAuditFromCloud(auditId).catch(() => {});
    } catch (err) {
      console.error(`Failed to delete audit ${auditId}:`, err);
    }
  }

  /**
   * Get active audit ID
   */
  static getActiveAuditId(): string | null {
    return localStorage.getItem(CURRENT_AUDIT_KEY);
  }

  /**
   * Set active audit ID
   */
  static setActiveAuditId(auditId: string): void {
    localStorage.setItem(CURRENT_AUDIT_KEY, auditId);
  }
}
